
import Vue from "vue";
import { Auth } from "aws-amplify";
import { prepareContactData, ContactData } from "@/lib/utils/contact";
import { ContactNotice, getActiveContactNotice } from "@/lib/config/contactNotices";

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo() {
    if (process.env.NODE_ENV === "production") {
      return {};
    }
    return {
      meta: [
        {
          name: "robots",
          content: "none",
        },
      ],
    };
  },
  data() {
    return {
      userId: "",
      message: "",
      idPoolId: "",
      showFallback: false,
      mailTo: "nola.cs@indent.co.jp",
      mailSubject: "",
      mailBody: "",
      contactData: null as ContactData | null,
    };
  },
  computed: {
    activeNotice(): ContactNotice | null {
      return getActiveContactNotice();
    },
    diagInfo() {
      const BR = "\n";
      const text = `${this.$t("contact.diagInfo.typeLabel")}${BR}${this.$t("contact.diagInfo.typeOptions")}${BR}${this.$t("contact.diagInfo.typeNote")}${BR}${BR}${this.$t("contact.diagInfo.occurredDateLabel")}${BR}${this.$t("contact.diagInfo.occurredDateExample")}${BR}${this.$t("contact.diagInfo.occurredDateNote")}${BR}${BR}${this.$t("contact.diagInfo.detailsLabel")}${BR}${this.$t("contact.diagInfo.detailsPlaceholder")}${BR}${BR}${this.$t("contact.diagInfo.bugTips")}${BR}${this.$t("contact.diagInfo.bugExample")}${BR}${this.$t("contact.diagInfo.manuscriptNote")}${BR}${this.$t("contact.diagInfo.fishNote")}${BR}${this.$t("contact.diagInfo.subscriptionNote")}${BR}${BR}${this.$t("contact.diagInfo.requiredForBug")}${BR}${BR}${this.$t("contact.diagInfo.browserInfoLabel")}${BR}${window.navigator.userAgent}${BR}${this.$t("contact.diagInfo.deviceInfoLabel")}${BR}${window.navigator.platform}${BR}${this.$t("contact.diagInfo.userIdLabel")}${BR}${this.userId}${BR}${this.$t("contact.diagInfo.investigationInfoLabel")}${BR}${this.idPoolId}${BR}`;
      return text;
    },
    mail(): string {
      if (!this.contactData) {
        return `mailto:${this.mailTo}`;
      }

      return `mailto:${this.mailTo}?subject=${encodeURIComponent(this.contactData.subject)}&body=${encodeURIComponent(
        this.contactData.body
      )}`;
    },
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
  },
  async created() {
    let userId = null;
    try {
      const user = await Auth.currentUserPoolUser();
      userId = user.signInUserSession.idToken.payload.sub;
    } catch (error) {
      // do nothing
    }
    this.userId = userId ?? this.$t("contact.notLoggedIn");

    const currentCredentials = (await Auth.currentCredentials()) as any;
    if (currentCredentials && currentCredentials.data) {
      this.idPoolId = currentCredentials.data.IdentityId;
    }

    // お問い合わせデータを事前生成
    const baseSubject = this.$t("contact.email.subject");
    const body = this.getEmailBody();
    this.contactData = prepareContactData(baseSubject, body, this.userId);
  },
  methods: {
    /**
     * メール本文を生成する共通メソッド
     */
    getEmailBody(): string {
      const BR = "\n";
      return `${this.$t("contact.email.bodyPrefix")}${BR}${BR}${this.diagInfo}`;
    },

    /**
     * フォールバック情報を表示
     */
    showFallbackOptions() {
      if (!this.contactData) {
        return;
      }

      // 事前生成されたデータを使用
      this.mailSubject = this.contactData.subject;
      this.mailBody = this.contactData.body;

      // フォールバック表示
      this.showFallback = true;
      this.message = "";
    },

    /**
     * 指定された要素の内容をクリップボードにコピー
     */
    copyToClipboard(type?: string) {
      let el: HTMLElement;
      let successMessage = "";

      switch (type) {
        case "mailTo":
          el = this.$refs.mailToInput as HTMLElement;
          successMessage = this.$t("contact.copyMessages.toSuccess");
          break;
        case "subject":
          el = this.$refs.subjectInput as HTMLElement;
          successMessage = this.$t("contact.copyMessages.subjectSuccess");
          break;
        case "body":
          el = this.$refs.bodyTextarea as HTMLElement;
          successMessage = this.$t("contact.copyMessages.bodySuccess");
          break;
        default:
          // 調査用情報のコピー（既存機能）
          el = this.$refs.textarea as HTMLElement;
          successMessage = this.$t("contact.copyMessages.diagInfoSuccess");
      }

      this.performCopy(el, successMessage);
    },

    /**
     * すべてのメール情報をコピー
     */
    copyAllMailInfo() {
      const allInfo = `${this.$t("contact.fallbackEmailInfo.toLabel")}${this.mailTo}\n${this.$t("contact.fallbackEmailInfo.subjectLabel")}${this.mailSubject}\n\n${this.$t("contact.fallbackEmailInfo.bodyLabel")}\n${this.mailBody}`;

      // 一時的なテキストエリアを作成してコピー
      const tempTextarea = document.createElement("textarea");
      tempTextarea.value = allInfo;
      document.body.appendChild(tempTextarea);

      this.performCopy(tempTextarea, this.$t("contact.copyMessages.allSuccess"));

      document.body.removeChild(tempTextarea);
    },

    /**
     * 実際のコピー処理
     */
    performCopy(element: HTMLElement, successMessage: string) {
      try {
        const el = element as HTMLInputElement | HTMLTextAreaElement;

        if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
          // iOS対応
          const range = document.createRange();
          el.contentEditable = "true";
          el.readOnly = false;
          range.selectNodeContents(el);

          const s = window.getSelection();
          s!.removeAllRanges();
          s!.addRange(range);

          if (el.setSelectionRange) {
            el.setSelectionRange(0, 999999);
          }

          el.contentEditable = "false";
          el.readOnly = true;
        } else {
          el.select();
        }

        if (document.execCommand("copy")) {
          this.message = successMessage;
        } else {
          this.message = this.$t("contact.copyMessages.failure");
        }
      } catch (error) {
        console.error("コピー処理でエラーが発生しました:", error);
        this.message = this.$t("contact.copyMessages.failure");
      }
    },

    /**
     * フォールバック表示を隠す
     */
    hideFallback() {
      this.showFallback = false;
      this.message = "";
    },
  },
});
